import React, { useState, useEffect } from 'react';
import { Layout, Button, Space, Input, message, ConfigProvider, theme, Dropdown, Avatar, Menu, App as AntdApp, Drawer } from 'antd';
import {
  FileText,
  Plus,
  ArrowLeft,
  Settings as SettingsIcon,
  Home,
  Moon,
  Sun,
  User,
  BarChart2,
  Bot,
  LogOut,
  Crown,
  Menu as MenuIcon
} from 'lucide-react';
import Dashboard from './components/Dashboard';
import EditorNew from './components/EditorNew';
import Settings from './components/Settings';
import UserDashboard from './components/UserDashboard';
import avatarService from './services/avatarService';
import AiChat from './components/AiChat';
import AuthQQ from './components/AuthQQ';
import NotePreview from './components/NotePreview';
import DownloadAppModal from './components/DownloadAppModal';
import DownloadFloatingButton from './components/DownloadFloatingButton';
import { DocumentManager } from './utils/DocumentManager';
import { SettingsManager, AppSettings } from './utils/SettingsManager';
import { Document } from './types';
import cacheManager from './services/cacheManager';
import authService, { User as AuthUser } from './services/authService';
import noteService, { Note } from './services/noteService';

const { Header, Content } = Layout;
const { Search: AntSearch } = Input;

type ViewType = 'dashboard' | 'editor' | 'settings' | 'user' | 'preview';

const App: React.FC = () => {
  const [currentView, setCurrentView] = useState<ViewType>('dashboard');
  const [currentDocument, setCurrentDocument] = useState<Document | null>(null);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [notes, setNotes] = useState<Note[]>([]);

  const [availableTags, setAvailableTags] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [settings, setSettings] = useState<AppSettings>(SettingsManager.getSettings());
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [editorSource, setEditorSource] = useState<'new' | 'preview' | 'direct'>('direct');
  const [appLoaded, setAppLoaded] = useState(false);

  // 移动端状态
  const [isMobile, setIsMobile] = useState(false);
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);

  // 检测移动端
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // 检查登录状态
  useEffect(() => {
    const checkAuthStatus = () => {
      // 调试日志：可在控制台看到本次校验来源
      try { console.debug('[Auth] checkAuthStatus running'); } catch { }
      // 同时检查 localStorage 与 sessionStorage，兼容未勾选“记住我”的场景
      let token: string | null = null;
      try {
        token = localStorage.getItem('token');
      } catch { }
      if (!token) {
        try {
          token = sessionStorage.getItem('token');
        } catch { }
      }
      const currentUser = authService.getCurrentUser();

      if (token && currentUser) {
        setUser(currentUser);
        setIsAuthenticated(true);
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
    };

    checkAuthStatus();

    // 监听用户信息更新
    const handleUserInfoUpdate = () => {
      checkAuthStatus();
    };

    // 监听localStorage变化（跨标签页）
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'token' || e.key === 'user') {
        checkAuthStatus();
      }
    };

    window.addEventListener('userInfoUpdated', handleUserInfoUpdate);
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('userInfoUpdated', handleUserInfoUpdate);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      loadNotes();
      loadTags();
    } else {
      loadDocuments();
    }
  }, [isAuthenticated]);

  // 在应用启动时初始化缓存管理器
  useEffect(() => {
    if (isAuthenticated) {
      console.log('App: 开始初始化缓存管理器');
      cacheManager.initialize().catch(error => {
        console.error('初始化缓存管理器失败:', error);
      });
    }
  }, [isAuthenticated]);

  // 应用初始化完成后隐藏加载动画
  useEffect(() => {
    const hideLoadingAnimation = () => {
      // 确保DOM已经渲染
      setTimeout(() => {
        // 使用全局函数隐藏加载动画
        if (typeof (window as any).hideLoadingAnimation === 'function') {
          (window as any).hideLoadingAnimation();
        } else {
          document.body.classList.add('app-loaded');
        }
        setAppLoaded(true);
      }, 100);
    };

    // 无论是否登录，应用初始化完成后都隐藏加载动画
    hideLoadingAnimation();
  }, []);

  // 当认证状态确定后，确保加载动画被隐藏
  useEffect(() => {
    // 优先使用强制隐藏函数
    if (typeof (window as any).forceHideLoading === 'function') {
      (window as any).forceHideLoading();
    } else if (typeof (window as any).hideLoadingAnimation === 'function') {
      (window as any).hideLoadingAnimation();
    }
  }, [isAuthenticated]);

  const loadDocuments = () => {
    const docs = DocumentManager.getAllDocuments();
    setDocuments(docs);
  };

  const loadNotes = async () => {
    try {
      const notesData = await noteService.getAllNotes();
      setNotes(notesData);
    } catch (error) {
      message.error('加载笔记失败');
    }
  };



  const loadTags = async () => {
    try {
      const tagsData = await noteService.getTags();
      setAvailableTags(tagsData);
    } catch (error) {
      // 静默处理加载标签失败
    }
  };

  const handleSettingsChange = (newSettings: AppSettings) => {
    setSettings(newSettings);
    SettingsManager.saveSettings(newSettings);
  };

  const handleNewDocument = async () => {
    if (isAuthenticated) {
      await handleNewNote();
    } else {
      const newDoc = DocumentManager.createDocument('新文档', '', '默认');
      setCurrentDocument(newDoc);
      setEditorSource('new');
      setCurrentView('editor');
      loadDocuments();
    }
  };

  const handleNewNote = async () => {
    try {
      const defaultTag = availableTags.length > 0 ? availableTags[0] : null;
      const newNote = await noteService.createNote({
        title: '新笔记',
        content: '',
        tagId: defaultTag?.id || null
      });

      const docFormat: Document = {
        id: newNote.id.toString(),
        title: newNote.title,
        content: newNote.content,
        category: '默认',
        tags: newNote.tag ? [newNote.tag.name] : [],
        tagId: newNote.tagId,
        createdAt: new Date(newNote.createdAt),
        updatedAt: new Date(newNote.updatedAt),
      };

      setCurrentDocument(docFormat);
      setEditorSource('new');
      setCurrentView('editor');
      await loadNotes();
    } catch (error) {
      console.error('创建笔记失败:', error);
      message.error('创建笔记失败');
    }
  };

  const handleDocumentSelect = (doc: Document) => {
    setCurrentDocument(doc);
    setCurrentView('preview');
  };

  const handleNoteSelect = async (note: Note) => {
    const docFormat: Document = {
      id: note.id.toString(),
      title: note.title,
      content: note.content,
      category: '默认',
      tags: note.tag ? [note.tag.name] : [],
      tagId: note.tagId,
      createdAt: new Date(note.createdAt),
      updatedAt: new Date(note.updatedAt),
    };
    setCurrentDocument(docFormat);
    setCurrentView('preview');
  };

  const handleSaveDocument = async (doc: Document) => {
    if (isAuthenticated) {
      try {
        await noteService.updateNote(parseInt(doc.id), {
          title: doc.title,
          content: doc.content,
          tagId: doc.tagId
        });
        await loadNotes();
        // 更新缓存
        cacheManager.updateCache('noteStats').catch(err => console.warn('更新笔记统计缓存失败:', err));
        cacheManager.updateCache('noteUsage').catch(err => console.warn('更新笔记使用情况缓存失败:', err));
        message.success('保存成功');
      } catch (error) {
        console.error('保存笔记失败:', error);
        message.error('保存失败');
      }
    } else {
      DocumentManager.saveDocument(doc);
      loadDocuments();
      message.success('保存成功');
    }
  };

  const handleDeleteDocument = async (docId: string) => {
    if (isAuthenticated) {
      try {
        await noteService.deleteNote(parseInt(docId));
        await loadNotes();
        message.success('删除成功');
      } catch (error) {
        console.error('删除笔记失败:', error);
        message.error('删除失败');
      }
    } else {
      DocumentManager.deleteDocument(docId);
      loadDocuments();
      message.success('删除成功');
    }
  };

  const handleEditDocument = () => {
    setEditorSource('preview');
    setCurrentView('editor');
  };

  const handleBackToDashboard = () => {
    setCurrentView('dashboard');
    setCurrentDocument(null);
  };

  const handleShowVipModal = () => {
    setCurrentView('user');
  };

  // 注意：网页端 NotePreview 不再需要 getCategoryColor，这里移除无用函数

  const filteredDocuments = isAuthenticated
    ? notes.filter(note =>
      note.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      note.content.toLowerCase().includes(searchTerm.toLowerCase())
    ).map(note => ({
      id: note.id.toString(),
      title: note.title,
      content: note.content,
      category: '默认',
      tags: note.tag ? [note.tag.name] : [],
      tagId: note.tagId,
      createdAt: new Date(note.createdAt),
      updatedAt: new Date(note.updatedAt),
    }))
    : documents.filter(doc =>
      doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.content.toLowerCase().includes(searchTerm.toLowerCase())
    );

  const handleAuthSuccess = () => {
    const currentUser = authService.getCurrentUser();
    setUser(currentUser);
    setIsAuthenticated(true);
    loadNotes();
  };

  const handleLogout = () => {
    authService.logout();
    setUser(null);
    setIsAuthenticated(false);
    setCurrentView('dashboard');
    setCurrentDocument(null);
    setSearchTerm('');
    setNotes([]);
    setDocuments([]);
    setAvailableTags([]);
  };

  // 如果未登录，显示登录界面
  if (!isAuthenticated) {
    return (
      <ConfigProvider
        theme={{
          algorithm: settings.darkMode ? theme.darkAlgorithm : theme.defaultAlgorithm,
        }}
      >
        <AuthQQ onAuthSuccess={handleAuthSuccess} />
      </ConfigProvider>
    );
  }

  const userMenu = (
    <Menu
      items={[
        {
          key: '1',
          label: `${user?.username}`,
          icon: <User size={16} />,
          disabled: true,
        },
        {
          key: '2',
          label: '退出登录',
          icon: <LogOut size={16} />,
          onClick: handleLogout,
        },
      ]}
    />
  );

  return (
    <ConfigProvider
      theme={{
        algorithm: settings.darkMode ? theme.darkAlgorithm : theme.defaultAlgorithm,
      }}
    >
      <AntdApp>
        <div data-theme={settings.darkMode ? 'dark' : 'light'} className="web-layout">
          <Layout className="app-container">
            {/* 网页版头部 */}
            <Header className="web-header" style={{
              background: settings.darkMode ? '#141414' : '#fff',
              borderBottom: `1px solid ${settings.darkMode ? '#434343' : '#e8e8e8'}`,
              padding: '0 24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div className="logo" style={{
                display: 'flex',
                alignItems: 'center',
                fontSize: '18px',
                fontWeight: 600,
                color: settings.darkMode ? '#fff' : '#1890ff'
              }}>
                <img
                  src="/icons/icon-192.png"
                  alt="小酷笔记"
                  style={{
                    width: '24px',
                    height: '24px',
                    marginRight: '8px',
                    borderRadius: '4px'
                  }}
                />
                小酷笔记
              </div>

              <Space size="middle">
                {/* 移动端显示返回按钮和菜单按钮 */}
                {isMobile ? (
                  <>
                    {currentView !== 'dashboard' && (
                      <Button
                        icon={<ArrowLeft size={16} />}
                        onClick={handleBackToDashboard}
                        title="返回首页"
                        className="mobile-keep"
                      />
                    )}
                    <Button
                      type="primary"
                      icon={<Plus size={16} />}
                      onClick={handleNewDocument}
                      title="新建文档"
                      className="mobile-keep"
                    />
                    <Button
                      icon={<MenuIcon size={16} />}
                      onClick={() => setMobileMenuVisible(true)}
                      title="菜单"
                      className="mobile-menu-trigger mobile-keep"
                    />
                  </>
                ) : (
                  /* 桌面端显示完整导航 */
                  <>
                    {currentView !== 'dashboard' && (
                      <Button
                        icon={<ArrowLeft size={16} />}
                        onClick={handleBackToDashboard}
                        title="返回首页"
                      >
                        返回
                      </Button>
                    )}
                    <Button
                      icon={<Home size={16} />}
                      onClick={() => setCurrentView('dashboard')}
                      type={currentView === 'dashboard' ? 'primary' : 'default'}
                      title="首页"
                    />
                    <Button
                      icon={<BarChart2 size={16} />}
                      onClick={() => setCurrentView('user')}
                      type={currentView === 'user' ? 'primary' : 'default'}
                      title="个人中心"
                    />
                    <Button
                      icon={<SettingsIcon size={16} />}
                      onClick={() => setCurrentView('settings')}
                      type={currentView === 'settings' ? 'primary' : 'default'}
                      title="设置"
                    />
                    <Button
                      icon={settings.darkMode ? <Sun size={16} /> : <Moon size={16} />}
                      onClick={() => handleSettingsChange({ ...settings, darkMode: !settings.darkMode })}
                      title="切换主题"
                    />
                    <Button
                      type="primary"
                      icon={<Plus size={16} />}
                      onClick={handleNewDocument}
                      title="新建文档"
                    >
                      新建文档
                    </Button>
                    <Dropdown overlay={userMenu} placement="bottomRight">
                      <Button
                        shape="circle"
                        icon={
                          <Avatar
                            size="small"
                            src={avatarService.getDisplayAvatarUrl(user?.avatar, user?.username)}
                            style={{ backgroundColor: '#1890ff' }}
                          >
                            {!user?.avatar && user?.username.charAt(0).toUpperCase()}
                          </Avatar>
                        }
                      />
                    </Dropdown>
                  </>
                )}
              </Space>
            </Header>

            <Content className="web-content" style={{ height: 'calc(100vh - 64px)' }}>
              {currentView === 'dashboard' ? (
                <Dashboard
                  documents={filteredDocuments}
                  onDocumentSelect={handleDocumentSelect}
                  onDocumentDelete={handleDeleteDocument}
                  settings={settings}
                />
              ) : currentView === 'editor' ? (
                <EditorNew
                  docData={currentDocument!}
                  onSave={handleSaveDocument}
                  settings={settings}
                  editorSource={editorSource}
                  onShowVipModal={handleShowVipModal}
                />
              ) : currentView === 'user' ? (
                <UserDashboard />
              ) : currentView === 'preview' && currentDocument ? (
                <NotePreview
                  document={currentDocument}
                  onEdit={handleEditDocument}
                  onBack={handleBackToDashboard}
                  settings={settings}
                />
              ) : (
                <Settings
                  onSettingsChange={handleSettingsChange}
                />
              )}
            </Content>
          </Layout>

          {/* 移动端底部导航栏 */}
          {isMobile && (
            <div className="mobile-bottom-nav">
              <Button
                type={currentView === 'dashboard' ? 'primary' : 'text'}
                icon={<Home size={18} />}
                onClick={() => setCurrentView('dashboard')}
              >
                首页
              </Button>
              <Button
                type={currentView === 'user' ? 'primary' : 'text'}
                icon={<BarChart2 size={18} />}
                onClick={() => setCurrentView('user')}
              >
                个人中心
              </Button>
              <Button
                type={currentView === 'settings' ? 'primary' : 'text'}
                icon={<SettingsIcon size={18} />}
                onClick={() => setCurrentView('settings')}
              >
                设置
              </Button>
              <Button
                type="text"
                icon={settings.darkMode ? <Sun size={18} /> : <Moon size={18} />}
                onClick={() => handleSettingsChange({ ...settings, darkMode: !settings.darkMode })}
              >
                主题
              </Button>
            </div>
          )}

          {/* 移动端侧边抽屉菜单 */}
          <Drawer
            title="菜单"
            placement="right"
            onClose={() => setMobileMenuVisible(false)}
            open={mobileMenuVisible}
            width={280}
          >
            <Space direction="vertical" style={{ width: '100%' }} size="large">
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <Avatar
                  size={64}
                  src={avatarService.getDisplayAvatarUrl(user?.avatar, user?.username)}
                  style={{ backgroundColor: '#1890ff', marginBottom: 12 }}
                >
                  {!user?.avatar && user?.username.charAt(0).toUpperCase()}
                </Avatar>
                <div style={{ fontSize: 16, fontWeight: 600 }}>
                  {user?.username}
                </div>
                {user?.isVipActive && (
                  <div style={{ color: '#faad14', fontSize: 12, marginTop: 4 }}>
                    <Crown size={12} style={{ marginRight: 4 }} />
                    VIP用户
                  </div>
                )}
              </div>

              <Button
                block
                size="large"
                type={currentView === 'dashboard' ? 'primary' : 'default'}
                icon={<Home size={16} />}
                onClick={() => {
                  setCurrentView('dashboard');
                  setMobileMenuVisible(false);
                }}
              >
                首页
              </Button>

              <Button
                block
                size="large"
                type={currentView === 'user' ? 'primary' : 'default'}
                icon={<BarChart2 size={16} />}
                onClick={() => {
                  setCurrentView('user');
                  setMobileMenuVisible(false);
                }}
              >
                个人中心
              </Button>

              <Button
                block
                size="large"
                type={currentView === 'settings' ? 'primary' : 'default'}
                icon={<SettingsIcon size={16} />}
                onClick={() => {
                  setCurrentView('settings');
                  setMobileMenuVisible(false);
                }}
              >
                设置
              </Button>

              <Button
                block
                size="large"
                icon={settings.darkMode ? <Sun size={16} /> : <Moon size={16} />}
                onClick={() => {
                  handleSettingsChange({ ...settings, darkMode: !settings.darkMode });
                  setMobileMenuVisible(false);
                }}
              >
                切换主题
              </Button>

              <Button
                block
                size="large"
                danger
                icon={<LogOut size={16} />}
                onClick={() => {
                  handleLogout();
                  setMobileMenuVisible(false);
                }}
              >
                退出登录
              </Button>
            </Space>
          </Drawer>

          {/* 右下角浮动下载按钮 */}
          <DownloadFloatingButton />
        </div>
      </AntdApp>
    </ConfigProvider>
  );
};

export default App;
